import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import {
  DatePicker,
  Radio,
  Button,
  Spin,
  Empty,
  Input,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';
import AnimateHeight from 'react-animate-height';
// My Stuff
import { useAppContext } from "../lib/contextLib";
import {
  filterCardsDataByTheirTextContent,
  filterCardsWithinDates,
  areDatesNOTSet,
  filterCardsBySearchTxtAndDates,
  sortAlphabeticallyByTitleProp,
  sortByStartDateProp,
  strContainsOnlySpacesTabsOrLineBreaks,
  areDeepEqual,
} from './utils';
import ClassForm from './ClassForm';
import config from '../config';
import ClassCard from './ClassCard';
import './ClassListMain.scss';
import { user } from '../lib/authLib';

const { RangePicker } = DatePicker;
let hasInitialSortHappened = false;

export default function ClassListMain() {
  const alphabetical = 'Alphabetical';
  const sortByDate = 'Date';
  const { isAuthenticated } = useAppContext();
  const initialDateRangePickerVals = [moment().subtract(1, 'month'), moment().add(1, 'month')];

  /* eslint-disable-next-line no-unused-vars */
  const [cardsData, setCardsData] = useState({});
  const [displayedCardsData, setDisplayedCardsData] = useState({});
  const [search, setSearch] = useState('');
  const [radioVal, setRadioVal] = useState(sortByDate);
  const [dates, setDates] = useState(initialDateRangePickerVals);

  const [showCreateClassFormDropdown, setShowCreateClassFormDropdown] = useState(0);
  const dropdownAnimationTime = 0; // ms

  useEffect(() => { // Load initial class data
    getClasses();
  }, []);

  useEffect(() => { // Once class data is loaded and rendered, sort & render only classes between initialDateRangePickerVals
    if (!hasInitialSortHappened) {
      const isEmptyObject = obj => JSON.stringify(obj) === JSON.stringify({});
      const noCardsDisplayedYet = isEmptyObject(displayedCardsData);
      const noCardsDataLoaded = isEmptyObject(cardsData);
      const cardsDataHasLoadedAndRendered = !noCardsDisplayedYet && !noCardsDataLoaded;
  
      if (cardsDataHasLoadedAndRendered) {
        onRangePickerChange(initialDateRangePickerVals, cardsData);
        hasInitialSortHappened = true;
      }
    }
  }, [displayedCardsData, cardsData]);

  function setClassesUI(cards, displayedCards) {
    setCardsData(cards);
    setDisplayedCardsData(displayedCards ? displayedCards : cards);
  }

  async function getClasses() {
    try {
      let classInfo;
      if (isAuthenticated && user[0].role !== 'student') {
        const currentUserSession = await Auth.currentSession();
        classInfo = await window.fetch(`${config.serverUrl}classes/allclassrosters/${user[0].email}`, {
          method: 'get',
          headers: {
            'Content-Type': 'application/json',
            'accesstoken': currentUserSession.getAccessToken().getJwtToken(),
          },
        });
        const allClassesData = await classInfo.json();
        setClassesUI(allClassesData);
      } else {
        classInfo = await window.fetch(`${config.serverUrl}classes/allclasses`, {
          method: 'get',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const allClassesData = await classInfo.json();
        const allClassesWithClassClosedDateInFuture = allClassesData.filter(({ classClosedDate }) => {
          return moment(classClosedDate, "MM-DD-YYYY").diff(moment()) > 0;
        });
        setClassesUI(allClassesWithClassClosedDateInFuture);
      }
      
      return '';
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('Error!\n', error);
      return error;
    }
  }

  /**
   * This function sets a new radio button based on the user's click event.
   *
   * @param {(objects[]|null[])} bothDates - The pair of dates, usually as Moment.js objects.
   * @param {objects[]} allTheCardsData - Array of all the classes/cards.
   *
   * @example
   *
   *      onChange={(bothDatesMoment, bothDateStrs) => onRangePickerChange(bothDatesMoment, cardsData)}
   */
  /* eslint-disable-next-line no-unused-vars */
  function onRangePickerChange(bothDates, allTheCardsData) {
    if (!Array.isArray(allTheCardsData)) return;
    const datesWereReset = bothDates === null;
    const pairOfDates = datesWereReset ? ['', ''] : bothDates;

    setDates(pairOfDates);

    setDisplayedCardsData(
      datesWereReset
        ? filterCardsDataByTheirTextContent(allTheCardsData, search)
        : filterCardsBySearchTxtAndDates(allTheCardsData, search, pairOfDates),
    );
  }

  /**
   * This function changes the displayed cards when the user types in text.
   *
   * @param {object} e - The text input change event.
   *
   * @example
   *
   *     onChangeSearchText(e)
   */
  function onChangeSearchText(e) {
    if (!Array.isArray(cardsData)) return;
    const { value } = e.target;
    const searchTextShouldNotFilterCards = (
      value.length < 1 || strContainsOnlySpacesTabsOrLineBreaks(value)
    );

    if (searchTextShouldNotFilterCards) {
      const [firstDate, secondDate] = dates;
      const datesAreSet = !areDatesNOTSet(firstDate, secondDate);
      const cardsToDisplay = datesAreSet ? filterCardsWithinDates(dates, cardsData) : cardsData;

      if (!areDeepEqual(displayedCardsData, cardsToDisplay)) {
        setDisplayedCardsData(cardsToDisplay);
      }
    } else {
      const [firstDate, secondDate] = dates;

      const totalFilteredCards = areDatesNOTSet(firstDate, secondDate)
        ? filterCardsDataByTheirTextContent(cardsData, value)
        : filterCardsBySearchTxtAndDates(cardsData, value, dates);

      setDisplayedCardsData(totalFilteredCards);
    }

    setSearch(value);
  }

  /**
   * This function sets a new radio button based on the user's click event.
   *
   * @param {object} e - The radio button change event.
   *
   * @example
   *
   *     onChange={onChangeRadio}
   */
  function onChangeRadio(e) {
    const { value } = e.target;

    setRadioVal(value);
  }

  const isThereClassCardsDataToShow = displayedCardsData.length > 0;

  return (
    <div className="ClassListMain">
      <Input
        className="text-search"
        value={search}
        onChange={(e) => onChangeSearchText(e)}
        placeholder="Search dance classes..."
      />
      <div className="radio-and-date-picker-container">
        <div className="radio-container">
          <Radio.Group
            defaultValue={radioVal}
            onChange={onChangeRadio}
            value={radioVal}
          >
            <Radio value={alphabetical}>Alphabetical</Radio>
            <Radio value={sortByDate}>Start Date (Descending)</Radio>
          </Radio.Group>
        </div>
        <div className="date-picker-container">
          <p>Show any class that starts between:</p>
          <RangePicker
            value={dates}
            onChange={(bothDatesMoment, bothDateStrs) => onRangePickerChange(bothDatesMoment, cardsData)}
          />
        </div>
      </div>
      {
        (isAuthenticated && user[0].role === 'admin') && (
          <>
            <Button
              onClick={() => {
                setShowCreateClassFormDropdown(showCreateClassFormDropdown ? 0 : 'auto');
              }}
              type="primary"
            >
              + Add a New Class
            </Button>
            <AnimateHeight
              duration={dropdownAnimationTime}
              height={showCreateClassFormDropdown}
            >
              <ClassForm
                getClasses={getClasses}
                closeClassForm={() => { setShowCreateClassFormDropdown(0); }}
              />
            </AnimateHeight>
          </>
        )
      }
      {
        isThereClassCardsDataToShow
          ? displayedCardsData
            .sort(
              radioVal === alphabetical
                ? sortAlphabeticallyByTitleProp
                : sortByStartDateProp,
            )
            .map((cardData) => (
              <ClassCard
                allCardsData={cardsData}
                setClassesUI={setClassesUI}
                allDisplayedCardsData={displayedCardsData}
                getClasses={getClasses}
                key={cardData.uuid}
                card={cardData}
              />
            ))
          : (
            Object.keys(cardsData).length > 0
            ? <Empty description="No Classes Found :-("/>
            : <Spin indicator={<LoadingOutlined style={{ fontSize: 94 }} spin />} className="spinner-placeholder" />
          )
      }
    </div>
  );
}
